import { useMemo, type ComponentProps } from 'react';
import {
  DEAL_LIST_VIEWS,
  FORECAST_CATEGORIES,
  type Deal,
  type DealListTotals,
  type DealListView,
} from '@forecastify/shared';
import {
  DataList,
  type DataListColumn,
  type DataListRow,
  type MenuItem,
  type ViewSwitch,
} from '@forecastify/ui';
import { useI18n, useT } from '../../i18n';
import { useDuration } from '../../duration';
import { relativeTime } from '../relative-time';
import { listLabels } from '../delivery/clients-screen';
import type { ListQuery } from '../use-list-query';
import { FORECAST_COLORS, dealMoney, forecastLabel, initialsOf, type DealFeeds } from './deal-kit';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D3 · *Dealek — lista* · `phase6/01-deals-list.png`│
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * One `DataList`, the mockup's fourteen columns and ten views. Views, filters, search and sort
 * are the server's (`deal.service.ts` `list`), and the footer's sums are the whole filtered set's
 * rather than the page's.
 *
 * ⚠️ **R-57 · the first column is the deal's public identifier**, `deal_<8 hex>` (DM-3) — the
 * mockup's `D-104` is a human number this product does not have.
 */

export interface DealRowActions {
  readonly onOpen: (deal: Deal) => void;
  readonly onFavourite: (deal: Deal, on: boolean) => void;
  readonly onWon: (deal: Deal) => void;
  readonly onLost: (deal: Deal) => void;
  readonly onReopen: (deal: Deal) => void;
  readonly onGenerate: (deal: Deal) => void;
  readonly onDelete: (deal: Deal) => void;
}

export function dealRowMenu(
  t: ReturnType<typeof useT>,
  deal: Deal,
  canManage: boolean,
  actions: DealRowActions,
): MenuItem[] {
  return [
    {
      label: t('deals.action.open'),
      icon: 'arrow-right',
      onSelect: () => {
        actions.onOpen(deal);
      },
    },
    {
      label: deal.favourite ? t('deals.action.unfavourite') : t('deals.action.favourite'),
      icon: 'star',
      onSelect: () => {
        actions.onFavourite(deal, !deal.favourite);
      },
    },
    ...(canManage
      ? deal.statusCategory === 'open'
        ? [
            {
              label: t('deals.action.won'),
              icon: 'circle-check' as const,
              onSelect: () => {
                actions.onWon(deal);
              },
            },
            {
              label: t('deals.action.lost'),
              icon: 'x' as const,
              onSelect: () => {
                actions.onLost(deal);
              },
            },
          ]
        : [
            {
              label: t('deals.action.reopen'),
              icon: 'refresh-cw' as const,
              onSelect: () => {
                actions.onReopen(deal);
              },
            },
          ]
      : []),
    ...(canManage
      ? [
          {
            label: t('deals.action.generate'),
            icon: 'sparkles' as const,
            onSelect: () => {
              actions.onGenerate(deal);
            },
          },
          {
            label: t('deals.action.delete'),
            icon: 'trash' as const,
            destructive: true,
            onSelect: () => {
              actions.onDelete(deal);
            },
          },
        ]
      : []),
  ];
}

export function viewLabel(t: ReturnType<typeof useT>, view: DealListView): string {
  if (view === 'all') return t('deals.view.all');
  if (view === 'mine') return t('deals.view.mine');
  if (view === 'rot') return t('deals.view.rot');
  if (view === 'nonext') return t('deals.view.nonext');
  if (view === 'quarter') return t('deals.view.quarter');
  if (view === 'slip') return t('deals.view.slip');
  if (view === 'won') return t('deals.view.won');
  if (view === 'lost') return t('deals.view.lost');
  if (view === 'conv') return t('deals.view.conv');
  return t('deals.view.open');
}

export function DealsListScreen({
  deals,
  totals,
  feeds,
  query,
  page,
  loading,
  modeSwitch,
  canManage,
  actions,
  onCreate,
  onOmitMany,
}: {
  readonly deals: readonly Deal[];
  readonly totals: DealListTotals | null;
  readonly feeds: DealFeeds;
  readonly query: ListQuery;
  readonly page: ComponentProps<typeof DataList>['page'];
  readonly loading: boolean;
  readonly modeSwitch: ViewSwitch;
  readonly canManage: boolean;
  readonly actions: DealRowActions;
  readonly onCreate: () => void;
  readonly onOmitMany: (ids: readonly string[]) => void;
}) {
  const t = useT();
  const { fmt } = useDuration();
  const { locale } = useI18n();
  const view = (query.filters.view ?? 'open') as DealListView;

  const rows: DataListRow[] = useMemo(
    () =>
      deals.map((deal) => ({
        id: deal.id,
        cells: {
          identifier: { text: deal.identifier, mono: true, muted: true },
          name: {
            text: deal.name,
            bold: true,
            dot: deal.rotting ? 'var(--destructive)' : (deal.clientColor ?? undefined),
            ...(deal.rotting ? { title: t('deals.rotting', { count: deal.daysInStage }) } : {}),
          },
          client: { text: deal.clientName, muted: true },
          status: { badge: { label: deal.statusName, variant: 'secondary' as const } },
          probability: { text: `${String(deal.probability)}%`, mono: true },
          forecast: {
            text: forecastLabel(t, deal.forecastCategory),
            dot: FORECAST_COLORS[deal.forecastCategory],
            muted: true,
          },
          value: { text: dealMoney(deal.value, deal.currencyCode), mono: true },
          weighted: {
            text: dealMoney(deal.weightedValue, deal.currencyCode),
            mono: true,
            muted: true,
          },
          hours: {
            text: Number(deal.plannedHours) > 0 ? fmt(deal.plannedHours) : '–',
            mono: true,
            muted: Number(deal.plannedHours) === 0,
          },
          owner: {
            avatar: {
              initials: initialsOf(deal.ownerName),
              title: deal.ownerName,
              seed: deal.ownerMemberId,
            },
            text: deal.ownerName,
            muted: true,
          },
          close: {
            text: deal.closeDate ?? '–',
            mono: true,
            muted: deal.closeDate === null,
            ...(deal.slipped ? { dot: 'var(--warning)' } : {}),
          },
          next:
            deal.nextStep === null
              ? { text: t('deals.next.none'), muted: true }
              : {
                  text:
                    deal.nextStep.due === null
                      ? deal.nextStep.text
                      : `${deal.nextStep.text} · ${deal.nextStep.due}`,
                  muted: !deal.nextStepLate,
                  ...(deal.nextStepLate
                    ? { dot: 'var(--destructive)', title: t('deals.next.late') }
                    : {}),
                },
          stage: { text: t('deals.days', { count: deal.daysInStage }), mono: true, muted: true },
          activity: {
            text: deal.lastActivityAt === null ? '–' : relativeTime(deal.lastActivityAt, locale),
            muted: true,
          },
        },
      })),
    [deals, t, locale, fmt],
  );

  const columns: DataListColumn[] = [
    { key: 'identifier', label: t('deals.column.identifier'), width: 120 },
    { key: 'name', label: t('deals.column.name'), sortKey: 'name' },
    { key: 'client', label: t('deals.column.client'), sortKey: 'client' },
    { key: 'status', label: t('deals.column.status'), width: 140, sortKey: 'status' },
    {
      key: 'probability',
      label: t('deals.column.probability'),
      width: 80,
      align: 'right',
      sortKey: 'probability',
    },
    { key: 'forecast', label: t('deals.column.forecast'), width: 130 },
    { key: 'value', label: t('deals.column.value'), width: 140, align: 'right', sortKey: 'value' },
    { key: 'weighted', label: t('deals.column.weighted'), width: 140, align: 'right' },
    { key: 'hours', label: t('deals.column.hours'), width: 90, align: 'right' },
    { key: 'owner', label: t('deals.column.owner'), width: 160, sortKey: 'owner' },
    { key: 'close', label: t('deals.column.close'), width: 120, sortKey: 'close' },
    { key: 'next', label: t('deals.column.next') },
    { key: 'stage', label: t('deals.column.stage'), width: 100, align: 'right', sortKey: 'stage' },
    { key: 'activity', label: t('deals.column.activity'), width: 130 },
  ];

  const openStatuses = (feeds.config?.statuses ?? []).filter(
    (status) => status.category === 'open',
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1">
        <DataList
          columnsKey="deals"
          card={{
            title: 'name',
            context: 'client',
            defaults: {
              badge: 'status',
              details: ['value', 'probability', 'close', 'next', 'owner'],
            },
          }}
          isLoading={loading}
          columns={columns}
          rows={rows}
          viewSwitch={modeSwitch}
          views={DEAL_LIST_VIEWS.map((entry) => ({ value: entry, label: viewLabel(t, entry) }))}
          view={view}
          onViewChange={(value) => {
            query.setFilter('view', value);
          }}
          actions={
            canManage
              ? [
                  {
                    label: t('deals.action.create'),
                    icon: 'plus' as const,
                    primary: true,
                    onSelect: onCreate,
                  },
                ]
              : []
          }
          sort={query.sort}
          onSortChange={query.onSortChange}
          search={query.search}
          onSearchChange={query.setSearch}
          filters={[
            {
              key: 'status',
              kind: 'multi' as const,
              label: t('deals.filter.status'),
              value: query.filters.status ?? '',
              onChange: (value) => {
                query.setFilter('status', value);
              },
              options: openStatuses.map((status) => ({ value: status.id, label: status.name })),
            },
            {
              key: 'owner',
              kind: 'multi' as const,
              label: t('deals.filter.owner'),
              value: query.filters.owner ?? '',
              onChange: (value) => {
                query.setFilter('owner', value);
              },
              options: feeds.members.map((member) => ({
                value: member.id,
                label: member.displayName,
              })),
            },
            {
              key: 'client',
              kind: 'multi' as const,
              label: t('deals.filter.client'),
              value: query.filters.client ?? '',
              onChange: (value) => {
                query.setFilter('client', value);
              },
              options: feeds.clients.map((client) => ({ value: client.id, label: client.name })),
            },
            {
              key: 'forecast',
              kind: 'multi' as const,
              label: t('deals.filter.forecast'),
              value: query.filters.forecast ?? '',
              onChange: (value) => {
                query.setFilter('forecast', value);
              },
              options: FORECAST_CATEGORIES.map((category) => ({
                value: category,
                label: forecastLabel(t, category),
              })),
            },
          ]}
          bulkActions={
            canManage
              ? [
                  {
                    label: t('deals.action.omit'),
                    icon: 'eye-off' as const,
                    onSelect: (ids: string[]) => {
                      onOmitMany(ids);
                    },
                  },
                ]
              : undefined
          }
          rowMenu={(id) => {
            const deal = deals.find((candidate) => candidate.id === id);
            return deal === undefined ? [] : dealRowMenu(t, deal, canManage, actions);
          }}
          onRowClick={(id) => {
            const deal = deals.find((candidate) => candidate.id === id);
            if (deal !== undefined) actions.onOpen(deal);
          }}
          footerNote={
            totals === null
              ? undefined
              : t('deals.footer', {
                  count: totals.count,
                  value: dealMoney(totals.value, totals.currencyCode),
                  weighted: dealMoney(totals.weighted, totals.currencyCode),
                  committed: dealMoney(totals.committed, totals.currencyCode),
                })
          }
          empty={{
            icon: 'tag',
            title: view === 'open' ? t('deals.empty.title') : t('deals.empty.filtered'),
            body: t('deals.empty.body'),
            actionLabel: canManage ? t('deals.action.create') : undefined,
            onAction: canManage ? onCreate : undefined,
          }}
          {...(page === undefined ? {} : { page })}
          labels={listLabels(t)}
        />
      </div>
    </div>
  );
}
